import { Fragment, useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen } from 'lucide-react';
import { GameVariant, VARIANT_SPECS, forzaLevel, type BallotTable } from '@draughts/shared';
import PuzzlePreviewBoard from '../shared/PuzzlePreviewBoard';
import CorniceDamiera from '../shared/CorniceDamiera';
import { enumerateOpenings, openingStatusCounts, distinctPositions, type EnumeratedOpening } from '../../rules/allOpenings';
import { buildRulesPath, isRulesLang, type RulesLang } from '../../seo/routes';
import './Rules.css';

interface RulesOpeningsPageProps {
  variant: GameVariant;
  /** Back to the variant's rules page. */
  onBack: () => void;
}

/** Tabs: the general table, the four federal ones, and every legal opening. */
const TABS = ['all', 'A', 'B', 'C', 'D', 'every'] as const;
type Tab = (typeof TABS)[number];

/** Columns in the row tables — the inline preview spans all of them. */
const OPENING_COLUMNS = 4;

export default function RulesOpeningsPage({ variant, onBack }: RulesOpeningsPageProps) {
  const { t, i18n } = useTranslation();
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';

  const spec = VARIANT_SPECS[variant];
  const lower = variant.toLowerCase();
  const name = t(`game.variants.${lower}`);

  // All 302 legal openings; the ballot ones carry their official entry.
  const openings = useMemo(() => enumerateOpenings(variant), [variant]);
  const listed = useMemo(() => openings.filter((o) => o.status === 'ballot'), [openings]);
  const status = useMemo(() => openingStatusCounts(openings), [openings]);
  const positions = useMemo(() => distinctPositions(openings), [openings]);

  const [tab, setTab] = useState<Tab>('all');
  const [selected, setSelected] = useState<EnumeratedOpening | null>(null);

  // Per-variant board palette. Hoisted out of the row loop: it is constant for
  // the page, and the vars inherit from .rl-board-wrap down into the SVG.
  const boardVars = useMemo(
    () =>
      ({
        '--rules-sq-dark': spec.ui.colors.darkSquare,
        '--rules-sq-light': spec.ui.colors.lightSquare,
        '--rules-piece-white': spec.ui.colors.whitePiece,
        '--rules-piece-black': spec.ui.colors.blackPiece,
      }) as CSSProperties,
    [spec],
  );

  // The panel opens below the fold when the tapped row is near the bottom.
  // A callback ref rather than an effect: picking another opening unmounts this
  // row and mounts a new one, so it fires exactly once, at the right moment.
  // `block: 'nearest'` is a no-op when the panel is already visible, and the
  // default `inline: 'nearest'` leaves the table's sideways scroll alone.
  // Optional-called: jsdom has no scrollIntoView.
  const revealDetail = useCallback((node: HTMLTableRowElement | null) => {
    node?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const rows = useMemo(() => {
    if (tab === 'every') return openings;
    if (tab === 'all') return listed;
    return listed.filter((o) => o.ballot!.tables.includes(tab as BallotTable));
  }, [openings, listed, tab]);

  // Grouped by first half-move: 302 rows in one list is unreadable, and the
  // groups are exactly how the printed table is organised.
  const groups = useMemo(() => {
    const byMove = new Map<string, EnumeratedOpening[]>();
    for (const o of rows) {
      const first = o.moves[0]!;
      const list = byMove.get(first);
      if (list) list.push(o);
      else byMove.set(first, [o]);
    }
    return [...byMove.entries()];
  }, [rows]);

  const counts = useMemo(() => {
    const c: Record<Tab, number> = { all: listed.length, A: 0, B: 0, C: 0, D: 0, every: openings.length };
    for (const o of listed) for (const table of o.ballot!.tables) c[table] += 1;
    return c;
  }, [listed, openings]);

  const tableDescKey = variant === GameVariant.ENGLISH ? 'english' : 'italian';

  /** Last column: the federal tables, or why the table leaves this opening out. */
  const reason = (o: EnumeratedOpening) => {
    if (o.status === 'ballot') return <span className="rl-op-tags">{o.ballot!.tables.join(' ')}</span>;
    if (o.status === 'transposition') {
      return <span className="rl-op-why">{t('rules.openings.statusTransposition', { id: o.sameAs })}</span>;
    }
    return <span className="rl-op-why">{t(`rules.openings.status_${o.status}`)}</span>;
  };

  return (
    <div className="rl-page">
      <div className="rl-inner">
        <div className="rl-crumb">
          <a
            href={buildRulesPath(lang, variant)}
            onClick={(e) => { e.preventDefault(); onBack(); }}
          >
            <span aria-hidden="true">←</span> {name}
          </a>
          <span className="rl-sep">/</span>
          <span className="rl-now">{t('rules.openings.title')}</span>
        </div>

        <div className="rl-card rl-hero rl-hero-3">
          <div className="rl-hero-deco"><div className="rl-hero-king" /></div>
          <div className="rl-hero-center">
            <span className="rl-eyebrow">{t('rules.openings.title')}</span>
            <h1 className="rl-hero-title">
              <span className="rl-flag" aria-hidden="true">{spec.ui.flagEmoji}</span> {name}
            </h1>
            <p className="rl-hero-sub">{t('rules.openings.heroSubtitle', { variant: name })}</p>
          </div>
          <div className="rl-hero-chip">
            <span className="rl-chip-ico"><BookOpen /></span>
            <span className="rl-chip-txt">
              <span className="rl-chip-v">{listed.length}</span>
              <span className="rl-chip-l">{t('rules.openings.colOpening')}</span>
            </span>
          </div>
        </div>

        <div className="rl-card rl-op-intro">
          <p>{t('rules.openings.intro')}</p>
          <p>{t('rules.openings.ratedNote')}</p>
          <p className="rl-op-source">{t('rules.openings.source')}</p>
        </div>

        {/* Legend: the strength codes are meaningless without it, and it has to
            be readable before the table on a phone. */}
        <div className="rl-card rl-op-legend">
          <span className="rl-eyebrow">{t('rules.openings.legendTitle')}</span>
          <ul>
            <li><span className="rl-forza rl-forza-0">PARI</span> {t('rules.openings.legendPari')}</li>
            <li><span className="rl-forza rl-forza-1">1N/1B</span> {t('rules.openings.legend1')}</li>
            <li><span className="rl-forza rl-forza-2">2N/2B</span> {t('rules.openings.legend2')}</li>
            <li><span className="rl-forza rl-forza-3">3N/3B</span> {t('rules.openings.legend3')}</li>
            <li><span className="rl-forza rl-forza-4">4N/4B</span> {t('rules.openings.legend4')}</li>
          </ul>
          <p className="rl-op-source">{t('rules.openings.legendColours')}</p>
        </div>

        <div className="rl-card rl-op-tables">
          <span className="rl-eyebrow">{t('rules.openings.tablesTitle')}</span>
          <div className="rl-rows">
            {(['A', 'B', 'C', 'D'] as const).map((table) => (
              <div key={table} className="rl-stat rl-op-tablerow">
                <span className="rl-lbl">
                  {t(`rules.openings.table${table}`)}
                  <span className="rl-op-count">{t('rules.openings.count', { count: counts[table] })}</span>
                </span>
                <span className="rl-val">{t(`rules.openings.${tableDescKey}${table}`)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Why 302 legal openings become a table of 174. */}
        <div className="rl-card rl-op-why-card">
          <span className="rl-eyebrow">{t('rules.openings.everyTitle')}</span>
          <p>{t('rules.openings.everyIntro', { total: openings.length, positions, listed: listed.length })}</p>
          <div className="rl-rows">
            <div className="rl-stat rl-op-tablerow">
              <span className="rl-lbl">
                {t('rules.openings.status_ballot')}
                <span className="rl-op-count">{status.ballot}</span>
              </span>
              <span className="rl-val">{t('rules.openings.statusBallotDesc')}</span>
            </div>
            <div className="rl-stat rl-op-tablerow">
              <span className="rl-lbl">
                {t('rules.openings.statusTranspositionShort')}
                <span className="rl-op-count">{status.transposition}</span>
              </span>
              <span className="rl-val">{t('rules.openings.statusTranspositionDesc')}</span>
            </div>
            <div className="rl-stat rl-op-tablerow">
              <span className="rl-lbl">
                {t('rules.openings.status_material')}
                <span className="rl-op-count">{status.material}</span>
              </span>
              <span className="rl-val">{t('rules.openings.statusMaterialDesc')}</span>
            </div>
            <div className="rl-stat rl-op-tablerow">
              <span className="rl-lbl">
                {t('rules.openings.status_unlisted')}
                <span className="rl-op-count">{status.unlisted}</span>
              </span>
              <span className="rl-val">{t('rules.openings.statusUnlistedDesc')}</span>
            </div>
            <div className="rl-stat rl-op-tablerow">
              <span className="rl-lbl">
                {t('rules.openings.tableD')}
                <span className="rl-op-count">{counts.D}</span>
              </span>
              <span className="rl-val">{t('rules.openings.statusDecidedDesc')}</span>
            </div>
          </div>
        </div>

        <div className="rl-op-list">
          {/* Default tab is the general table, so the prerendered HTML carries
              all 174 official rows; the tabs only filter what is already there. */}
          <div className="rl-op-tabs" role="tablist" aria-label={t('rules.openings.tablesTitle')}>
            {TABS.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                className={`rl-op-tab${tab === value ? ' active' : ''}`}
                // Drop the selection too: the previewed opening is usually
                // filtered out of the tab being switched to.
                onClick={() => {
                  setTab(value);
                  setSelected(null);
                }}
              >
                {value === 'all'
                  ? t('rules.openings.tableAll')
                  : value === 'every'
                    ? t('rules.openings.tableEvery')
                    : t(`rules.openings.table${value}`)}
                <span className="rl-op-count">{counts[value]}</span>
              </button>
            ))}
          </div>

          {groups.map(([firstMove, list]) => (
            <details key={firstMove} className="rl-card rl-op-group" open>
              <summary>
                {t('rules.openings.firstMove', { move: firstMove })}
                <span className="rl-op-count">{list.length}</span>
              </summary>
              <div className="rl-op-scroll">
                <table className="rl-op-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('rules.openings.colNumber')}</th>
                      <th scope="col">{t('rules.openings.colOpening')}</th>
                      {/* Le due intestazioni lunghe stanno dentro un <abbr>.
                          Maiuscoletto e spaziatura le rendevano piu' larghe del
                          loro contenuto (`1N/1B`, `A B`), e con `white-space:
                          nowrap` e' l'intestazione a fissare la larghezza minima
                          della colonna: a 360px la tabella sfondava e si doveva
                          scorrere di lato. Il `title` tiene la parola intera
                          nell'HTML precotto e la offre al passaggio del mouse. */}
                      <th scope="col">
                        <abbr title={t('rules.openings.colStrength')}>
                          {t('rules.openings.colStrengthShort')}
                        </abbr>
                      </th>
                      <th scope="col">
                        <abbr title={t('rules.openings.colTables')}>
                          {t('rules.openings.colTablesShort')}
                        </abbr>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((o) => {
                      const open = selected?.n === o.n;
                      return (
                        <Fragment key={o.n}>
                          <tr
                            className={`rl-op-row-${o.status}${open ? ' selected' : ''}`}
                            aria-expanded={open}
                            onClick={() => setSelected((prev) => (prev?.n === o.n ? null : o))}
                            /*
                             * La riga si apre anche da tastiera. Aveva già
                             * `aria-expanded` — cioè si annunciava come qualcosa
                             * che si espande — ma non era raggiungibile: nessun
                             * `tabIndex`, nessuna risposta a Invio. Promettere un
                             * comportamento e non offrirlo è peggio che non
                             * prometterlo.
                             *
                             * `tabIndex` e basta, NIENTE `role="button"`: dentro
                             * una tabella quel ruolo cancella il ruolo `row`, e
                             * uno screen reader smette di leggere la tabella come
                             * tabella — si guadagna un tasto e si perde la
                             * struttura. `aria-expanded` dice già cosa fa.
                             */
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' && e.key !== ' ') return;
                              e.preventDefault();
                              setSelected((prev) => (prev?.n === o.n ? null : o));
                            }}
                          >
                            <td className="rl-op-id">{o.ballot ? o.ballot.id : '–'}</td>
                            <td className="rl-op-moves">{o.moves.join(' ')}</td>
                            <td>
                              {o.ballot ? (
                                <span className={`rl-forza rl-forza-${forzaLevel(o.ballot.forza)}`}>
                                  {o.ballot.forza}
                                </span>
                              ) : (
                                <span className="rl-op-why">–</span>
                              )}
                            </td>
                            <td>{reason(o)}</td>
                          </tr>
                          {/* The board belongs next to the row that asked for it: a card
                              above the tables meant scrolling back to the top on every tap. */}
                          {open && (
                            <tr className="rl-op-detail" ref={revealDetail}>
                              <td className="rl-op-detail-cell" colSpan={OPENING_COLUMNS}>
                                <div className="rl-op-detail-panel">
                                  <span className="rl-eyebrow rl-op-detail-head">
                                    {o.ballot ? `#${o.ballot.id} · ` : ''}{o.moves.join(' ')}
                                  </span>
                                  <div className="rl-board-wrap rl-op-detail-board" style={boardVars}>
                                    <CorniceDamiera className="rl-cornice">
                                      <PuzzlePreviewBoard
                                        fen={o.fen}
                                        variant={lower}
                                        squareSize={30}
                                        className="rl-board-svg"
                                        darkSquareClass="rules-sq-dark"
                                        lightSquareClass="rules-sq-light"
                                        ariaLabel={`${t('rules.openings.colOpening')} ${o.moves.join(' ')}`}
                                      />
                                    </CorniceDamiera>
                                  </div>
                                  {/* At 360px the reason column sits past the edge of the scroll
                                      box, so the tap that opens the board also spells it out here. */}
                                  <p className="rl-op-verdict">
                                    {o.ballot
                                      ? `${t('rules.openings.colStrength')} ${o.ballot.forza} · ${o.ballot.tables
                                          .map((table) => t(`rules.openings.table${table}`))
                                          .join(', ')}`
                                      : o.status === 'transposition'
                                        ? t('rules.openings.statusTransposition', { id: o.sameAs })
                                        : t(`rules.openings.status_${o.status}`)}
                                  </p>
                                  <p className="rl-op-fen">{o.fen}</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
